import { Schema } from "effect";
import { MoneySchema, type Money } from "./Money";

/**
 * Pure domain logic for financial calculations (no external dependencies)
 * This belongs in the value object as it's pure business logic
 */

/**
 * Calculates the grand total based on it's smaller parts.
 *
 * @param subtotal
 * @param taxes
 * @param shippingCost
 * @param discount
 * @returns A {@link Money} object with the grand total and the currency of the parts.
 */
function calculateGrandTotal(
  subtotal: Money,
  taxes: Money,
  shippingCost: Money,
  discount: Money
): Money {
  const grandTotal = subtotal
    .addMultiple(taxes, shippingCost)
    .subtract(discount);
  return grandTotal.roundTwoDecimals();
}

/**
 * Pure domain validation logic (no external dependencies)
 */
function validateGrandTotalCalculation(financials: {
  subtotal: Money;
  taxes: Money;
  shippingCost: Money;
  discount: Money;
  grandTotal: Money;
}): boolean {
  try {
    const expectedGrandTotal = calculateGrandTotal(
      financials.subtotal,
      financials.taxes,
      financials.shippingCost,
      financials.discount
    );
    const tolerance = 0.01;
    return (
      Math.abs(financials.grandTotal.subtract(expectedGrandTotal).amount) <=
      tolerance
    );
  } catch (error) {
    console.error(
      "Could not validate grand total",
      error instanceof Error ? error.message : String(error)
    );
    // If we can't calculate the expected grand total (e.g., due to negative result),
    // then the validation should fail
    return false;
  }
}

/**
 * The schema for InvoiceFinancials with validation rules including grandTotal calculation validation.
 *
 * Note: This uses pure domain logic functions, not domain services, as it's part of
 * the value object's invariant validation.
 */
export const InvoiceFinancialsSchema = Schema.Struct({
  subtotal: MoneySchema,
  discount: MoneySchema,
  taxes: MoneySchema,
  shippingCost: MoneySchema,
  grandTotal: MoneySchema,
}).pipe(
  Schema.filter(
    (financials) => {
      return validateGrandTotalCalculation(financials);
    },
    {
      message: ({ actual }) => {
        return `Grand total must equal subtotal + taxes + shipping cost - discount. Got ${JSON.stringify(
          actual
        )}`;
      },
    }
  )
);

/**
 * Type derived from the schema
 */
export type InvoiceFinancials = Schema.Schema.Type<
  typeof InvoiceFinancialsSchema
>;

/**
 * Factory function to create and validate InvoiceFinancials
 */
export const createInvoiceFinancials = (
  data: unknown
): typeof InvoiceFinancialsSchema.Type =>
// ): Schema.Schema.Encoded<typeof InvoiceFinancialsSchema> =>
  Schema.decodeUnknownSync(InvoiceFinancialsSchema)(data);

/**
 * Validation function that returns an Effect
 */
export const validateInvoiceFinancials = (data: unknown) =>
  Schema.decodeUnknown(InvoiceFinancialsSchema)(data);

/**
 * Factory function to create InvoiceFinancials with automatic grand total calculation
 * This uses pure domain logic, not domain services
 */
export const createInvoiceFinancialsWithCalculation = (data: {
  subtotal: Money;
  taxes: Money;
  shippingCost: Money;
  discount: Money;
}): Schema.Schema.Encoded<typeof InvoiceFinancialsSchema> => {
  const grandTotal = calculateGrandTotal(
    data.subtotal,
    data.taxes,
    data.shippingCost,
    data.discount
  );
  return createInvoiceFinancials({
    ...data,
    grandTotal,
  });
};

/**
 * Export pure calculation function for use by domain services
 * This allows the domain service to use the same logic without circular dependencies
 */
export { calculateGrandTotal, validateGrandTotalCalculation };
